import React, { useEffect, useMemo, useRef, useState, Children } from 'react'
import Box from '@material-ui/core/Box'
import { makeStyles } from '@material-ui/core/styles'
import SliderControl from 'components/atoms/slider-control'

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    overflow: 'hidden',
  },
  viewport: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    overflow: 'hidden',
  },
  track: {
    display: 'flex',
    transition: 'transform 280ms ease',
    willChange: 'transform',
  },
  slide: {
    boxSizing: 'border-box',
    overflow: 'hidden',
    minWidth: 0,
    flexShrink: 0,
  },
  controls: {
    marginTop: theme.spacing(1),
  },
  atStart: {
    '& button:first-child': {
      opacity: 0.5,
      pointerEvents: 'none',
    },
  },
  atEnd: {
    '& button:last-child': {
      opacity: 0.5,
      pointerEvents: 'none',
    },
  },
}))

/**
 * Full-width, page-bounded carousel for JRL merch upsell cards.
 * Separate from SliderNew (keen peek / width:initial). Slide widths are
 * measured from the viewport so flex min-content cannot blow out parents.
 */
export default function JrlMerchSlider({ children }) {
  const classes = useStyles()
  const viewportRef = useRef(null)
  const [slideWidth, setSlideWidth] = useState(0)
  const slides = useMemo(() => Children.toArray(children).filter(Boolean), [
    children,
  ])
  const [index, setIndex] = useState(0)
  const maxIndex = Math.max(slides.length - 1, 0)
  const showControls = slides.length > 1

  useEffect(() => {
    const node = viewportRef.current
    if (!node || typeof ResizeObserver === 'undefined') return undefined

    const update = () => {
      setSlideWidth(Math.floor(node.getBoundingClientRect().width))
    }
    update()

    const observer = new ResizeObserver(update)
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  const goTo = next => {
    setIndex(Math.max(0, Math.min(maxIndex, next)))
  }

  const controlClassName = [
    index === 0 ? classes.atStart : '',
    index === maxIndex ? classes.atEnd : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <Box className={classes.root} data-testid="jrl-merch-slider">
      <Box className={classes.viewport} ref={viewportRef}>
        <Box
          className={classes.track}
          style={{
            transform:
              slideWidth > 0
                ? `translateX(-${index * slideWidth}px)`
                : `translateX(-${index * 100}%)`,
          }}
          data-testid="jrl-merch-slider-track"
        >
          {slides.map((child, slideIndex) => {
            const shouldPreload = Math.abs(index - slideIndex) <= 1
            const content =
              React.isValidElement(child) && child.props?.product
                ? React.cloneElement(child, {
                  isMerchPreloadCandidate: shouldPreload,
                })
                : child

            return (
              <Box
                key={child.key || slideIndex}
                className={classes.slide}
                data-testid="jrl-merch-slider-slide"
                style={
                  slideWidth > 0
                    ? { width: slideWidth, flex: `0 0 ${slideWidth}px` }
                    : { width: '100%', flex: '0 0 100%' }
                }
              >
                {content}
              </Box>
            )
          })}
        </Box>
      </Box>

      {showControls && (
        <Box className={classes.controls}>
          <SliderControl
            className={controlClassName}
            onPrevious={() => goTo(index - 1)}
            onNext={() => goTo(index + 1)}
          />
        </Box>
      )}
    </Box>
  )
}
